import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getAccountNotes,
  createAccountNote,
  updateAccountNote,
  deleteAccountNote,
} from '../api';

export const accountNoteKeys = {
  all: ['account-notes'] as const,
  list: (accountId: string, filters?: Record<string, unknown>) =>
    [...accountNoteKeys.all, accountId, 'list', filters] as const,
};

export function useAccountNotes(
  accountId: string,
  params?: { pageNumber?: number; pageLength?: number; category?: string }
) {
  return useQuery({
    queryKey: accountNoteKeys.list(accountId, params),
    queryFn: () => getAccountNotes(accountId, params),
    enabled: !!accountId,
  });
}

export function useCreateAccountNote(accountId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { content: string; category?: string }) =>
      createAccountNote(accountId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...accountNoteKeys.all, accountId] });
      toast.success('Note created');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to create note');
    },
  });
}

export function useUpdateAccountNote(accountId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, data }: { noteId: string; data: { content?: string; category?: string } }) =>
      updateAccountNote(accountId, noteId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...accountNoteKeys.all, accountId] });
      toast.success('Note updated');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update note');
    },
  });
}

export function useDeleteAccountNote(accountId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) => deleteAccountNote(accountId, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...accountNoteKeys.all, accountId] });
      toast.success('Note deleted');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to delete note');
    },
  });
}
